import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowPathIcon,
    CalendarDaysIcon,
    ExclamationTriangleIcon,
    MegaphoneIcon,
    BellAlertIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getMessagingStats } from "../api/messagingApi";
import type { MessagingStatsQueryParams } from "../types";

// ============================================================================
// Helpers
// ============================================================================

const fmtNumber = (n: number | null | undefined) =>
    n == null ? "—" : new Intl.NumberFormat("en-NG").format(n);

const fmtLabel = (s: string) =>
    s
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());

// ============================================================================
// Component
// ============================================================================

export const MessagingStatsRoute = () => {
    const [params, setParams] = useState<MessagingStatsQueryParams>({});

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ["messaging-stats", params],
        queryFn: () => getMessagingStats(params),
    });

    const stats = data?.data;

    // ========================================================================
    // Loading / Error
    // ========================================================================

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">
                    Failed to load messaging statistics
                </p>
            </div>
        );
    }

    // ========================================================================
    // Render
    // ========================================================================

    return (
        <div className="space-y-6">
            {/* Date range + refresh */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
                    <input
                        type="date"
                        value={params.startDate ?? ""}
                        onChange={(e) =>
                            setParams((p) => ({ ...p, startDate: e.target.value || undefined }))
                        }
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                    <span className="text-sm text-slate-400">to</span>
                    <input
                        type="date"
                        value={params.endDate ?? ""}
                        onChange={(e) =>
                            setParams((p) => ({ ...p, endDate: e.target.value || undefined }))
                        }
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                </div>
                <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            {stats && (
                <>
                    {/* ── Announcements ── */}
                    {stats.announcements && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                                <MegaphoneIcon className="h-4 w-4 text-primary-500" /> Announcements
                            </h3>
                            <div className="grid gap-4 sm:grid-cols-4">
                                {[
                                    { label: "Total", value: stats.announcements.total, color: "text-slate-900 dark:text-white" },
                                    { label: "Sent", value: stats.announcements.sent, color: "text-emerald-600 dark:text-emerald-400" },
                                    { label: "Scheduled", value: stats.announcements.scheduled, color: "text-blue-600 dark:text-blue-400" },
                                    { label: "Drafts", value: stats.announcements.draft, color: "text-slate-500 dark:text-slate-400" },
                                ].map((card) => (
                                    <div key={card.label} className="rounded-xl bg-slate-50 p-4 dark:bg-slate-800/50">
                                        <p className="text-xs text-slate-500 dark:text-slate-400">{card.label}</p>
                                        <p className={`mt-1 text-2xl font-bold ${card.color}`}>{fmtNumber(card.value)}</p>
                                    </div>
                                ))}
                            </div>

                            {/* By Type */}
                            {stats.announcements.byType && Object.keys(stats.announcements.byType).length > 0 && (
                                <div className="mt-4">
                                    <p className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">By Type</p>
                                    <div className="flex flex-wrap gap-2">
                                        {Object.entries(stats.announcements.byType).map(([type, count]) => (
                                            <span
                                                key={type}
                                                className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300"
                                            >
                                                {fmtLabel(type)}: {fmtNumber(count)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Push Notifications ── */}
                    {stats.pushNotifications && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                                <BellAlertIcon className="h-4 w-4 text-amber-500" /> Push Notifications
                            </h3>
                            <div className="grid gap-4 sm:grid-cols-4">
                                <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-800/50">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Total Sent</p>
                                    <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                                        {fmtNumber(stats.pushNotifications.totalSent)}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-800/50">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Delivered</p>
                                    <p className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                                        {fmtNumber(stats.pushNotifications.delivered)}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-800/50">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Failed</p>
                                    <p className="mt-1 text-2xl font-bold text-rose-600 dark:text-rose-400">
                                        {fmtNumber(stats.pushNotifications.failed)}
                                    </p>
                                </div>
                                <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-800/50">
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Delivery Rate</p>
                                    <p className="mt-1 text-2xl font-bold text-blue-600 dark:text-blue-400">
                                        {stats.pushNotifications.deliveryRate?.toFixed(1)}%
                                    </p>
                                </div>
                            </div>

                            {/* Delivery rate bar */}
                            <div className="mt-4">
                                <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                                    <div
                                        className="h-full rounded-full bg-emerald-500"
                                        style={{ width: `${stats.pushNotifications.deliveryRate ?? 0}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Date Range Footer */}
                    {stats.dateRange && (
                        <p className="text-xs text-slate-400">
                            Period: {stats.dateRange.startDate} – {stats.dateRange.endDate}
                        </p>
                    )}
                </>
            )}
        </div>
    );
};
